<script lang="ts">
    import { onMount } from "svelte"
    import { createEventDispatcher } from "svelte"
    import Icon from "../../../../widgets/Icon.svelte"
    import { mdiPencilOutline } from "@mdi/js"
    import { tooltip } from "../../../../../helper"
    import NumberField from "../../../../widgets/fields/NumberField.svelte"
    import { _ } from "svelte-i18n"
    export let cssClass: string = ""
    export let label: string
    // Zahlenfelder bekommen Zahlen, Text-/Beschreibungsfelder Strings
    // (z.B. service.service, service.description) – daher beides zulässig.
    export let value: number | string
    export let type: string = "text"
    export let readonly: boolean = false
    export let readonlyNumber: boolean = false
    export let required: boolean = false
    export let showAsEditable: boolean = false
    export let fieldMdiIcon: string = mdiPencilOutline
    export let inputMdiIcon: string = null
    export let invalid: boolean = false
    export let disabled = false
    export let showTwoDecimals = false
    export let showThreeDecimals = false
    export let showIcon = false
    export let priceWizard = false
    export let decimal = 2
    export let higherThanZero = false
    export let showEuroSymbol = false
    export let multipleLines = false
    export let row: HTMLElement = null
    export let maxlength: number = null
    let random = Math.random()
    let initialValue = value
    $: if (higherThanZero) {
        if (typeof value === "number" && value <= 0) value = 1
    }

    function toCents(value: number): number {
        if (decimal == 0) return Math.round(value)
        if (decimal == 2) return Math.round(value * 100)
        return Math.round(value * 1000)
    }

    function fromCents(value: number): number {
        if (decimal == 0) return Math.round(value)
        if (decimal == 2) return parseFloat((value / 100).toFixed(2))
        return parseFloat((value / 1000).toFixed(3))
    }

    function r(v: number) {
        return fromCents(toCents(v))
    }

    /**
     * Anzeigetext für ein Zahlenfeld. `value` darf laut Prop auch ein String
     * sein (Text-/Beschreibungsfelder), gerechnet wird aber nur mit Zahlen.
     */
    function anzeigeZahl(wert: number | string): string {
        const zahl = typeof wert === "number" ? wert : parseFloat(wert) || 0
        return showTwoDecimals
            ? (Math.round(zahl * 100) / 100).toFixed(2)
            : (Math.round(zahl * 1000) / 1000).toFixed(3)
    }

    // Wird je nach `multipleLines` an ein <input> oder ein <textarea> gebunden.
    let input: HTMLInputElement | HTMLTextAreaElement
    const dispatch = createEventDispatcher()
    let rawValue = value
    $: {
        // Beim Tippen eines Dezimalpunkts (z.B. "1.") darf nicht gerundet
        // werden, sonst verschwindet die Eingabe unter dem Cursor.
        if (typeof value === "string" && value[value.length - 1] === ".") {
        } else if (type == "number" && typeof value === "number") value = r(value)
    }

    function isValidNumber(str: string) {
        if (typeof str != "string") return false // we only process strings!
        return (
            !isNaN(Number(str)) && // Coercion des gesamten Strings (parseFloat allein tut das nicht)
            !isNaN(parseFloat(str))
        ) // ...and ensure strings of whitespace fail
    }

    const onEnter = (e: Event) => {
        if (type === "number") {
            const target = e.target as HTMLInputElement
            target.value = target.value.replace(/,/g, ".")
            if (showTwoDecimals || showThreeDecimals) {
                if (!isValidNumber(target.value)) {
                    if (higherThanZero) {
                        target.value = showTwoDecimals ? "1.00" : "1.000"
                        value = 1
                    } else {
                        target.value = showTwoDecimals ? "0.00" : "0.000"
                        value = 0
                    }
                    rawValue = 1
                    dispatch("updated", parseFloat(target.value))
                } else {
                    value = parseFloat(target.value)
                    rawValue = value
                    dispatch("updated", parseFloat(target.value))
                }
            } else if (!isValidNumber(target.value)) {
                if (higherThanZero) {
                    value = 1
                    target.value = "1"
                } else {
                    value = 0
                    target.value = "0"
                }
                invalid = false
                dispatch("updated", parseFloat(String(value)))
            } else {
                invalid = false
                dispatch("updated", parseFloat(target.value))
            }
        } else {
            const target = e.target as HTMLInputElement
            if (!target.value) {
                invalid = true
                dispatch("invalid")
            } else {
                invalid = false
                dispatch("updated", target.value) // Hier verwenden wir nicht parseFloat, da der Typ nicht "number" ist.
            }
        }
    }

    const onBlur = (e: Event) => {
        const target = e.target as HTMLInputElement
        if (
            (type == "text" ? target.value || "" : target.value) ===
            (type == "text" ? initialValue || "" : initialValue)
        )
            return
        value = target.value
        onEnter(e)
        if (type === "number") {
            if (target.value == "") {
                if (higherThanZero) {
                    target.value = "1"
                } else {
                    target.value = "0"
                }
            }
            // Für die weitere Rechnung eine Zahl halten; die gerundete
            // Anzeige ist ein String und wird nur dem Feld zugewiesen.
            const zahl = parseFloat(target.value)
            value = zahl
            if (showTwoDecimals) {
                value = Math.round(zahl * 100) / 100
            } else if (showThreeDecimals) {
                value = Math.round(zahl * 1000) / 1000
            }
            dispatch("blur", parseFloat(target.value))
            return
        }

        dispatch("blur", value)
    }

    const onKeyPress = (e: Event) => {
        if ((e as KeyboardEvent).key === "Enter") onBlur(e)
    }

    function autoResize() {
        // @ts-ignore
        this.style.height = "auto"
        // @ts-ignore
        this.style.height = this.scrollHeight + "px"
    }

    onMount(() => {
        if (multipleLines) {
            const textarea = document.getElementById("autoresizing-" + random)
            if (textarea) {
                textarea.addEventListener("input", autoResize, false)
                textarea.style.height = "auto"
                textarea.style.height = textarea.scrollHeight + "px"
            }
        }
    })

    $: if (row) {
        row.style.alignItems = "flex-start"
    }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="edit-field {cssClass}" data-cy="{label}" class:invalid="{invalid && required}" on:click on:keydown>
    <label for="">{label}</label>
    <div class="edit-field-input-group">
        {#if readonlyNumber}
            <NumberField value="{anzeigeZahl(value)}" unit="{showEuroSymbol ? '€' : ''}" />
        {:else}
            {#if showTwoDecimals || showThreeDecimals}
                <input
                    class="edit-field-input"
                    type="text"
                    class:showIcon
                    class:width-input-icon="{inputMdiIcon}"
                    value="{anzeigeZahl(value)}"
                    class:showed-as-editable="{showAsEditable}"
                    {readonly}
                    {disabled}
                    step="{showThreeDecimals ? 0.001 : 0.01}"
                    {required}
                    {maxlength}
                    bind:this="{input}"
                    on:click="{(e) => {
                        // @ts-ignore
                        if (e.target.value == 1) e.target.value = ''
                    }}"
                    on:blur="{onBlur}"
                    on:keypress="{onKeyPress}"
                />
            {:else if multipleLines}
                <textarea
                    style="text-align:{type == 'number' ? 'right' : 'left'}; min-height: 30px;padding-top: 4px; "
                    class="edit-field-input"
                    id="autoresizing-{random}"
                    rows="1"
                    class:width-input-icon="{inputMdiIcon}"
                    class:showIcon
                    {value}
                    class:showed-as-editable="{showAsEditable}"
                    {readonly}
                    {disabled}
                    {required}
                    {maxlength}
                    bind:this="{input}"
                    on:click="{(e) => {
                        // @ts-ignore
                        if (e.target.value == 1) e.target.value = ''
                    }}"
                    on:blur="{onBlur}"
                    on:keypress="{onKeyPress}"
                ></textarea>
            {:else}
                <input
                    style="text-align:{type == 'number' ? 'right' : 'left'};"
                    class="edit-field-input"
                    class:width-input-icon="{inputMdiIcon}"
                    class:showIcon
                    {value}
                    class:showed-as-editable="{showAsEditable}"
                    {readonly}
                    {maxlength}
                    step="0.001"
                    {disabled}
                    {required}
                    bind:this="{input}"
                    on:click="{(e) => {
                        // @ts-ignore
                        if (e.target.value == 1) e.target.value = ''
                    }}"
                    on:blur="{onBlur}"
                    on:keypress="{onKeyPress}"
                />{/if}
            {#if showEuroSymbol}€
            {/if}
            {#if showIcon}
                <div
                    style="width: 24px"
                    use:tooltip="{{
                        content: $_('components.priceWizard.editEndcustomerAddress'),
                    }}"
                >
                    {#if !disabled && fieldMdiIcon}
                        <Icon path="{fieldMdiIcon}" size="24px" />
                    {/if}
                </div>
            {/if}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
                style="width: 24px"
                on:click|preventDefault|stopPropagation="{() => {
                    if (!disabled && priceWizard) dispatch('triggerPricewizard')
                }}"
                use:tooltip="{{
                    content: $_('components.priceWizard.showPricewizard'),
                }}"
                on:keydown|stopPropagation
            >
                {#if !disabled && priceWizard}
                    <Icon path="{fieldMdiIcon}" size="24px" />
                {/if}
            </div>
        {/if}
    </div>
</div>
